import {
  Badge,
  Checkbox,
  Group,
  Menu,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import { useState } from 'react';

/**
 * Narrow by a column's values. Ticking nothing means no opinion, so clearing a filter is the same
 * gesture as never having set one.
 *
 * How many values are ticked shows on the heading, so a narrowed table never looks like a short one.
 */
export function ColumnFilter({
  label,
  choices,
  chosen,
  onToggle,
}: {
  label: string;
  choices: string[];
  chosen: string[];
  onToggle: (value: string) => void;
}) {
  const [search, setSearch] = useState('');
  const matching = choices.filter(choice =>
    choice.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const narrowing = chosen.length > 0;

  return (
    <Menu
      shadow="sm"
      width={240}
      closeOnItemClick={false}
      position="bottom-start"
      onClose={() => setSearch('')}
    >
      <Menu.Target>
        <UnstyledButton aria-label={`Filter by ${label}`} title={`Filter by ${label}`}>
          <Group gap={2} wrap="nowrap">
            <IconFilter
              size={13}
              opacity={narrowing ? 1 : 0.55}
              color={narrowing ? 'var(--mantine-color-blue-6)' : undefined}
            />
            {narrowing && (
              <Badge size="xs" circle variant="light">
                {chosen.length}
              </Badge>
            )}
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <TextInput
          size="xs"
          placeholder={`Search ${label}`}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          data-autofocus
          mb={4}
        />
        <ScrollArea.Autosize mah={260}>
          {matching.length === 0 && (
            <Text size="xs" c="dimmed" p="xs">
              Nothing matches “{search}”
            </Text>
          )}
          {matching.map(choice => (
            // The row is the click target and the indicator only draws the state. A real checkbox
            // here would toggle twice on a click, once itself and once through the row.
            <Menu.Item key={choice} onClick={() => onToggle(choice)}>
              <Group gap="xs" wrap="nowrap">
                <Checkbox.Indicator size="xs" checked={chosen.includes(choice)} />
                <Text size="xs">{choice}</Text>
              </Group>
            </Menu.Item>
          ))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}
